import { money, FigureNote, Notes, NotAvailable } from "./IntlBits";

const STATUS = { home: "Home students", eu_eea: "EU/EEA students", international: "International students", in_state: "In-state students", any: "All students", subject: "This subject" };
const PER = (unit) => (unit.endsWith("_per_month") ? " a month" : unit.endsWith("_per_year") ? " a year" : "");
const listYears = (ys) => (ys.length === 1 ? yrs(ys[0]) : `${ys.slice(0, -1).join(", ")} or ${ys[ys.length - 1]} years`);
const yrs = (y) => `${y} ${y === 1 ? "year" : "years"}`;

const Badge = ({ s, testId }) => (
  <span data-testid={testId} className="mr-2 inline-block rounded-full border border-stone-300 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-stone-600">{STATUS[s] || s}</span>
);

function CostRow({ f, testId }) {
  return (
    <li data-testid={testId} className="py-3">
      <div className="flex items-start justify-between gap-4">
        <div><Badge s={f.fee_status} testId={`${testId}-status`} /><span className="text-sm text-stone-800">{f.label}</span></div>
        <span data-testid={`${testId}-value`} className="whitespace-nowrap font-editorial text-lg text-stone-900">{money(f.value, f.unit)}<span className="text-xs text-stone-500">{PER(f.unit)}</span></span>
      </div>
      <p className="mt-1 text-[11px] text-stone-600">{f.eligibility}. {f.scope}. <span data-testid={`${testId}-period`}>{f.period}</span>.</p>
    </li>
  );
}

function CostSection({ title, kind, rows, c, id, className }) {
  const gaps = c.gaps.map((g, i) => ({ ...g, i })).filter((g) => g.kind === kind);
  if (!rows.length && !gaps.length) return null;
  return (
    <div data-testid={`${id}-${kind}`} className={className}>
      <p className="font-mono text-[10px] uppercase tracking-wider text-stone-500">{title}</p>
      <ul className="divide-y divide-stone-100">
        {rows.map((f) => <CostRow key={f.id} f={f} testId={`${id}-${f.id}`} />)}
        {gaps.map((g) => (
          <li key={g.i} data-testid={`${id}-gap-${g.i}`} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3">
            <Badge s={g.fee_status} testId={`${id}-gap-${g.i}-status`} />
            <NotAvailable testId={`${id}-gap-${g.i}-na`} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function CountryNotes({ c, id, sources }) {
  const dur = c.duration;
  return (
    <Notes testId={`${id}-notes`}>
      <ul className="space-y-1.5">
        {[...c.tuition, ...c.living].map((f) => <FigureNote key={f.id} f={f} sources={sources} testId={`${id}-${f.id}-source`} />)}
        {c.gaps.map((g, i) => (
          <li key={i} data-testid={`${id}-gap-${i}-reason`}>
            <span className="font-medium text-stone-800">{g.kind === "tuition_fee" ? "Tuition" : "Living costs"} · {STATUS[g.fee_status] || g.fee_status}: </span>Not available. {g.reason}
          </li>
        ))}
      </ul>
      {dur && dur.courses > 0 && (
        <p data-testid={`${id}-duration-detail`}>Course lengths in WUA records: {dur.common.map((x) => `${yrs(x.years)} (${x.courses} ${x.courses === 1 ? "course" : "courses"})`).join(", ")}, out of {dur.courses} bachelor's courses.</p>
      )}
    </Notes>
  );
}

function CountryCard({ c, sources }) {
  const id = `sci-intl-cost-${c.code.toLowerCase()}`;
  const dur = c.duration;
  return (
    <div data-testid={id} className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex items-baseline justify-between">
        <h3 className="font-editorial text-xl text-stone-900">{c.name}</h3>
        <span data-testid={`${id}-currency`} className="font-mono text-[11px] text-stone-500">{c.currency}</span>
      </div>
      <p data-testid={`${id}-duration`} className="mt-1 text-xs text-stone-600">
        {dur && dur.courses ? `Usual length: ${listYears(dur.common.map((x) => x.years).sort((a, b) => a - b))}` : "Course length: data not available"}
      </p>
      <CostSection title="Tuition" kind="tuition_fee" rows={c.tuition} c={c} id={id} className="mt-4" />
      <CostSection title="Living costs" kind="living_cost" rows={c.living} c={c} id={id} className="mt-3" />
      <CountryNotes c={c} id={id} sources={sources} />
    </div>
  );
}

export function CostsPanel({ costs, sources }) {
  return (
    <div data-testid="sci-intl-costs">
      <ul data-testid="sci-intl-costs-warnings" className="mb-6 list-disc space-y-1 rounded-lg border border-amber-200 bg-amber-50 py-3 pl-9 pr-4 text-xs text-amber-900">
        {costs.warnings.map((w) => <li key={w}>{w}</li>)}
      </ul>
      <div className="grid gap-5 lg:grid-cols-2">{costs.countries.map((c) => <CountryCard key={c.code} c={c} sources={sources} />)}</div>
      <Notes testId="sci-intl-costs-notes" label="About course lengths">
        <p data-testid="sci-intl-costs-duration-note">{costs.duration_note}</p>
      </Notes>
    </div>
  );
}
